import { useMemo, useState } from 'react';
import type { PostEnriched } from '@/lib/db/types';
import {
  FANTASY_HOLES,
  bestScoresByFantasyHole,
  buildHoleAnalytics,
  fantasyHolesForCampus,
  type FantasyHoleDef,
} from '@/lib/scoring/fantasy-card';
import { buildCareerRinger, type CareerRingerNine } from '@/lib/scoring/career-ringer';
import { collectFantasyGrossEntries } from '@/lib/scoring/fantasy-gross';
import { scoreToParLabel } from '@/lib/scoring/played-par';
import { Sheet } from '@/components/shared/Sheet';
import { ScoreNotation } from '@/components/play/ScoreNotation';

function scoreTone(score: number, par: number): string {
  const d = score - par;
  if (d < 0) return '#dc2626';
  return '#0a1e3f';
}

interface Props {
  posts: PostEnriched[];
  memberId: string;
  onClose: () => void;
}

function HoleDetail({
  holeId,
  entries,
  onBack,
}: {
  holeId: string;
  entries: ReturnType<typeof collectFantasyGrossEntries>;
  onBack: () => void;
}) {
  const analytics = useMemo(
    () => buildHoleAnalytics(holeId, entries),
    [entries, holeId],
  );

  if (!analytics) return null;
  const { hole, best, worst, average, roundsPlayed, birdies, pars, bogeys, history } = analytics;

  return (
    <div className="px-4 pb-8 pt-1">
      <button type="button" onClick={onBack} className="mb-3 text-xs font-bold text-navy">← Back to card</button>
      <div className="mb-4 rounded-2xl bg-navy p-4 text-center text-cream">
        <div className="text-[10px] font-extrabold tracking-[0.15em] text-gold-bright">{hole.campus.toUpperCase()} · {hole.nine.toUpperCase()} NINE</div>
        <div className="mt-1 font-serif text-4xl font-bold text-gold">Hole {hole.holeInNine}</div>
        <div className="mt-1 text-sm text-white/70">Par {hole.par}</div>
        {best != null && (
          <div className="mt-2 text-lg font-bold text-white">
            Lifetime best: {best} ({scoreToParLabel(best, hole.par)})
          </div>
        )}
      </div>
      <div className="mb-4 grid grid-cols-3 gap-2">
        {[
          { label: 'Avg', value: average ?? '—' },
          { label: 'Worst', value: worst ?? '—' },
          { label: 'Rounds', value: roundsPlayed },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-line bg-paper p-2.5 text-center">
            <div className="text-[9px] font-bold text-mute">{s.label.toUpperCase()}</div>
            <div className="mt-0.5 font-serif text-lg font-bold text-navy">{s.value}</div>
          </div>
        ))}
      </div>
      {roundsPlayed > 0 && (
        <div className="mb-4 flex justify-around rounded-xl border border-line bg-paper p-3 text-center text-xs">
          <div><span className="font-bold text-good">{birdies}</span><div className="text-mute">Birdies+</div></div>
          <div><span className="font-bold text-navy">{pars}</span><div className="text-mute">Pars</div></div>
          <div><span className="font-bold text-bad">{bogeys}</span><div className="text-mute">Bogeys+</div></div>
        </div>
      )}
      <div className="text-[10px] font-extrabold tracking-wide text-mute">HISTORY (GROSS)</div>
      {history.length === 0 ? (
        <div className="mt-2 text-xs text-mute">No recorded rounds on this hole yet.</div>
      ) : (
        <div className="mt-2 space-y-1.5">
          {history.slice(0, 12).map((h) => {
            const isBest = best != null && h.strokes === best;
            return (
              <div
                key={`${h.playedAt}-${h.strokes}-${h.courseName || ''}`}
                className="flex justify-between rounded-lg border border-line bg-paper px-3 py-2 text-xs"
              >
                <span className="text-ink-soft">
                  {new Date(h.playedAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                  {h.courseName && <span className="ml-1 text-mute">· {h.courseName.split('—').pop()?.trim()}</span>}
                  {isBest && <span className="ml-1.5 font-bold text-gold-deep">· best</span>}
                </span>
                <span className="font-bold" style={{ color: scoreTone(h.strokes, hole.par) }}>{h.strokes}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NineGrid({
  label,
  holes,
  best,
  onSelect,
}: {
  label: string;
  holes: FantasyHoleDef[];
  best: Record<string, number>;
  onSelect?: (id: string) => void;
}) {
  return (
    <div className="mb-4">
      {label ? <div className="mb-2 text-[10px] font-extrabold tracking-wide text-gold-deep">{label}</div> : null}
      <div className="grid grid-cols-9 gap-1">
        {holes.map((h) => {
          const v = best[h.id];
          const inner = (
            <>
              <span className="text-[8px] font-bold text-mute">{h.holeInNine}</span>
              <ScoreNotation score={v ?? null} par={h.par} size={24} className="mt-0.5" />
            </>
          );
          if (onSelect) {
            return (
              <button
                key={h.id}
                type="button"
                onClick={() => onSelect(h.id)}
                className="flex flex-col items-center rounded-lg border border-line bg-paper py-1.5"
              >
                {inner}
              </button>
            );
          }
          return (
            <div key={h.id} className="flex flex-col items-center rounded-lg border border-line bg-paper py-1.5">
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CareerRingerNineCard({ nine }: { nine: CareerRingerNine }) {
  return (
    <div className="mb-4 rounded-xl border border-line bg-paper p-3">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <div className="text-[13px] font-bold text-navy">{nine.nine} Nine · {nine.total}</div>
        <div className="text-[11px] font-semibold text-mute">{scoreToParLabel(nine.total, nine.parTotal)}</div>
      </div>
      <div className="text-[10px] leading-relaxed text-ink-soft">
        From <strong className="text-navy">one complete round</strong> — nine total only (not lifetime bests;
        those are on Fantasy Card above).
        {' · '}
        {new Date(nine.playedAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
        {nine.comboLabel && <span> · {nine.comboLabel}</span>}
      </div>
    </div>
  );
}

export function FantasyCardSheet({ posts, memberId, onClose }: Props) {
  const [selectedHole, setSelectedHole] = useState<string | null>(null);
  const [campusFilter, setCampusFilter] = useState<'All' | 'Arlington' | 'Fairfax'>('All');

  const roundPosts = useMemo(
    () => posts.filter((p) => p.type === 'round'),
    [posts],
  );

  const entries = useMemo(
    () => collectFantasyGrossEntries(posts, memberId),
    [posts, memberId],
  );

  const careerRinger = useMemo(
    () => buildCareerRinger(roundPosts, memberId),
    [roundPosts, memberId],
  );

  const best = bestScoresByFantasyHole(entries);
  const activeHoles = fantasyHolesForCampus(campusFilter);
  const holeCount = activeHoles.length;
  const filled = activeHoles.filter((h) => best[h.id] != null).length;
  const ringerTotal = activeHoles.reduce((sum, h) => sum + (best[h.id] ?? 0), 0);
  const parTotal = activeHoles.reduce((sum, h) => sum + h.par, 0);
  const isComplete = filled === holeCount;
  const campusScopeLabel = campusFilter === 'All'
    ? 'both campuses'
    : campusFilter;

  const campuses = campusFilter === 'All'
    ? (['Arlington', 'Fairfax'] as const)
    : [campusFilter];

  return (
    <Sheet title="Fantasy & Ringer Cards" onClose={onClose}>
      {selectedHole ? (
        <HoleDetail holeId={selectedHole} entries={entries} onBack={() => setSelectedHole(null)} />
      ) : (
        <div className="px-4 pb-8 pt-1">
          <section className="mb-8">
            <div className="mb-1 text-[10px] font-extrabold tracking-[0.15em] text-gold-deep">
              FANTASY CARD — BEST PER HOLE
            </div>
            <p className="mb-3 text-xs leading-relaxed text-ink-soft">
              Your lifetime best <strong className="text-navy">gross</strong> on every physical hole — one score per hole on this card, from any round
              {campusFilter === 'All'
                ? ' at Arlington and Fairfax (all combos combined).'
                : ` at ${campusFilter} (all combos combined).`}
              {' '}Blue 2 on Blue/Red is the same hole as Blue 2 on White/Blue. Tap any hole for history.
            </p>
            <p className="mb-3 text-[11px] font-semibold text-navy">
              Gross only — net scores are not used on Fantasy Card.
            </p>
            <div className="mb-3 flex gap-1.5">
              {(['All', 'Arlington', 'Fairfax'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCampusFilter(c)}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold ${
                    campusFilter === c ? 'bg-navy text-cream' : 'border border-line bg-paper text-ink-soft'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="mb-4 rounded-2xl bg-navy p-4 text-center text-cream">
              <div className="text-[10px] font-extrabold tracking-[0.15em] text-gold-bright">
                {holeCount}-HOLE RINGER · GROSS
              </div>
              <div className="mt-1 font-serif text-[42px] font-bold leading-none text-gold">
                {isComplete ? ringerTotal : `${ringerTotal}*`}
              </div>
              <div className="mt-1 text-xs text-white/70">
                {isComplete
                  ? `${scoreToParLabel(ringerTotal, parTotal)} vs par (par ${parTotal})`
                  : `${filled} of ${holeCount} holes recorded`}
              </div>
            </div>
            {campuses.map((campus) => (
              <div key={campus}>
                <div className="mb-2 font-serif text-sm font-bold text-navy">{campus}</div>
                {(['Blue', 'Red', 'White'] as const).map((nine) => {
                  const slice = FANTASY_HOLES.filter((h) => h.campus === campus && h.nine === nine);
                  return (
                    <NineGrid
                      key={`${campus}-${nine}`}
                      label={`${nine} Nine`}
                      holes={slice}
                      best={best}
                      onSelect={setSelectedHole}
                    />
                  );
                })}
              </div>
            ))}
            {!isComplete && (
              <div className="mt-1 text-[10.5px] italic text-mute">
                * Total covers your {filled} recorded holes across {campusScopeLabel}.
              </div>
            )}
          </section>

          <div className="mb-4 border-t border-line pt-6">
            <div className="mb-1 text-[10px] font-extrabold tracking-[0.15em] text-gold-deep">CAREER RINGER — BEST COMPLETE NINE</div>
            <p className="mb-4 text-xs leading-relaxed text-ink-soft">
              Your <strong className="text-navy">two best 9-hole gross totals</strong> at each campus — each nine comes from a single round (your lowest-total Blue, Red, or White nine). Includes 9-hole rounds. Gross only.
            </p>
            {careerRinger.every((c) => c.nines.length === 0) ? (
              <div className="py-6 text-center text-[13px] leading-relaxed text-mute">
                No 9-hole segments recorded yet. Post a round and your best nines will appear here.
              </div>
            ) : (
              careerRinger.map((campusCard) => (
                <div key={campusCard.campus} className="mb-6">
                  <div className="mb-2 font-serif text-sm font-bold text-navy">{campusCard.campus}</div>
                  {campusCard.nines.length === 0 ? (
                    <div className="mb-4 text-xs text-mute">No recorded nines at this campus yet.</div>
                  ) : (
                    <>
                      <div className="mb-3 rounded-2xl bg-navy p-4 text-center text-cream">
                        <div className="text-[10px] font-extrabold tracking-[0.15em] text-gold-bright">CAREER RINGER · GROSS</div>
                        <div className="mt-1 font-serif text-[36px] font-bold leading-none text-gold">
                          {campusCard.total}
                        </div>
                        <div className="mt-1 text-xs text-white/70">
                          {scoreToParLabel(campusCard.total, campusCard.parTotal)} vs par (par {campusCard.parTotal})
                        </div>
                        <div className="mt-1 text-[11px] text-white/55">
                          Best {campusCard.nines.length} nine{campusCard.nines.length === 1 ? '' : 's'} across all courses
                        </div>
                      </div>
                      {campusCard.nines.map((nine) => (
                        <CareerRingerNineCard key={nine.physicalNineKey} nine={nine} />
                      ))}
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Sheet>
  );
}
